import React from 'react';
import { FormControl, InputLabel, Select, MenuItem, Checkbox, Box, Chip, SelectChangeEvent } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface SelectableItem {
    id: string;
    name: string;
}

interface MultiSelectProps<T extends SelectableItem, K = string> {
    items: T[] | undefined;
    selectedIds: K[];
    onSelectChange: (event: SelectChangeEvent<K[]>) => void;
    translationKey: string;
    disabled?: boolean;
    required?: boolean;
    getLabel?: (item: T) => string;
    renderChip?: (item: T) => React.ReactNode;
}

export const MultiSelect = <T extends SelectableItem>({
    items,
    selectedIds,
    onSelectChange,
    translationKey,
    disabled = false,
    required = false,
    getLabel = (item) => item.name,
    renderChip,
}: MultiSelectProps<T>) => {
    const { t } = useTranslation();

    return (
        <FormControl fullWidth sx={{ my: 2 }}>
            <InputLabel
                sx={{
                    backgroundColor: 'background.paper',
                    px: 0.5,
                }}
            >
                {t(translationKey)}
            </InputLabel>
            <Select
                multiple
                value={selectedIds}
                onChange={onSelectChange}
                label={t(translationKey)}
                required={required}
                disabled={disabled}
                MenuProps={{
                    PaperProps: {
                        sx: {
                            maxHeight: 500,
                            '& .MuiMenuItem-root': {
                                fontWeight: 500,
                                py: 1,
                            },
                        },
                    },
                }}
                sx={{
                    '& .MuiSelect-select': {
                        minHeight: '30px',
                        display: 'flex',
                        alignItems: 'center',
                        py: 1,
                    },
                }}
                renderValue={(selected) => (
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 0.5,
                            py: 0.5,
                        }}
                    >
                        {selected.map((id) => {
                            const selectedItem = items?.find((item) => item.id === id);
                            return selectedItem ? (
                                renderChip ? (
                                    renderChip(selectedItem)
                                ) : (
                                    <Chip
                                        key={selectedItem.id}
                                        label={getLabel(selectedItem)}
                                        sx={{
                                            backgroundColor: 'primary.light',
                                            color: 'primary.contrastText',
                                            fontSize: '1rem',
                                            fontWeight: 600,
                                            '& .MuiChip-deleteIcon': {
                                                color: 'primary.contrastText',
                                                '&:hover': {
                                                    color: 'white',
                                                },
                                            },
                                        }}
                                    />
                                )
                            ) : null;
                        })}
                    </Box>
                )}
            >
                {items?.map((item) => (
                    <MenuItem
                        key={item.id}
                        value={item.id}
                        sx={{
                            fontWeight: selectedIds.includes(item.id) ? 600 : 400,
                            bgcolor: selectedIds.includes(item.id) ? 'action.selected' : 'inherit',
                        }}
                    >
                        <Checkbox
                            checked={selectedIds.includes(item.id)}
                            sx={{
                                color: 'primary.main',
                                '&.Mui-checked': {
                                    color: 'primary.main',
                                },
                            }}
                        />
                        {getLabel(item)}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};
